import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { openHireTimesheetPrint } from '@/components/pmv/PrintablePmvCommercial';
import { useHireMasterLabels } from '@/hooks/useHireMasterLabels';
import pmvHireService, {
  formatHireMoney,
  type PmvHireAgreement,
  type PmvHireTimesheet,
} from '@/services/pmv-hire.service';
import {
  HIRE_DOC_STATUS_COLOR,
  formatHireAgreementOption,
  formatHireCalculationOption,
  hireTableCellEllipsis,
  hireTableLinkBtnStyle,
  resolveLabel,
} from '@/utils/pmvHireUi';

const { Text } = Typography;

const PmvHireTimesheetPage: React.FC = () => {
  const { id } = useParams();
  const isDetail = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [status, setStatus] = useState<string | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();
  const watchedAgreementId = Form.useWatch('hire_agreement_id', form);

  const { supplierLabelById, equipmentLabelById } = useHireMasterLabels();

  const agreementsQ = useQuery({
    queryKey: ['pmv-hire-agreements-for-ts'],
    queryFn: () => pmvHireService.listAgreements({ limit: 200 }),
    staleTime: 30_000,
  });

  const agreementById = useMemo(() => {
    const m = new Map<string, PmvHireAgreement>();
    for (const a of agreementsQ.data?.items ?? []) m.set(a.id, a);
    return m;
  }, [agreementsQ.data]);

  const agreementOptions = useMemo(
    () =>
      (agreementsQ.data?.items ?? []).map((a) => ({
        value: a.id,
        label: formatHireAgreementOption(a),
      })),
    [agreementsQ.data]
  );

  const calcsQ = useQuery({
    queryKey: ['pmv-hire-calcs-for-ts', watchedAgreementId],
    queryFn: () =>
      pmvHireService.listCalculations({
        hire_agreement_id: watchedAgreementId,
        limit: 100,
      }),
    enabled: createOpen && !!watchedAgreementId,
  });

  const calcOptions = useMemo(
    () =>
      (calcsQ.data?.items ?? []).map((c) => ({
        value: c.id,
        label: formatHireCalculationOption(c),
      })),
    [calcsQ.data]
  );

  const listQ = useQuery({
    queryKey: ['pmv-hire-timesheets', status],
    queryFn: () => pmvHireService.listTimesheets({ status, limit: 100 }),
    enabled: !isDetail,
  });

  const detailQ = useQuery({
    queryKey: ['pmv-hire-timesheet', id],
    queryFn: () => pmvHireService.getTimesheet(id!),
    enabled: isDetail,
  });

  const createMut = useMutation({
    mutationFn: (body: { calculation_id: string; notes?: string }) =>
      pmvHireService.createTimesheet(body),
    onSuccess: (ts) => {
      message.success('Timesheet created');
      setCreateOpen(false);
      form.resetFields();
      navigate(`/asset/pmv/hire/timesheets/${ts.id}`);
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  const actionMut = useMutation({
    mutationFn: async (action: 'submit' | 'review' | 'certify') => {
      if (action === 'submit') return pmvHireService.submitTimesheet(id!);
      if (action === 'review') return pmvHireService.reviewTimesheet(id!, {});
      return pmvHireService.certifyTimesheet(id!);
    },
    onSuccess: () => {
      message.success('Updated');
      qc.invalidateQueries({ queryKey: ['pmv-hire-timesheet', id] });
      qc.invalidateQueries({ queryKey: ['pmv-hire-timesheets'] });
    },
    onError: (e: Error) => message.error(e.message || 'Action failed'),
  });

  if (isDetail) {
    const ts = detailQ.data;
    const agreement = ts?.hire_agreement_id ? agreementById.get(ts.hire_agreement_id) : undefined;
    const explanation =
      ts?.calculation?.explanation_json ||
      (ts as { explanation_json?: Record<string, unknown> } | undefined)?.explanation_json;

    const summaryEntries = explanation
      ? Object.entries(explanation).filter(
          ([, v]) => v == null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
        )
      : [];

    return (
      <WorkspaceLayout>
        <EnterprisePageHeader
          documentLabel="Hire Timesheet"
          documentNumber={ts?.timesheet_number || ts?.id?.slice(0, 8)}
          subtitle="Commercial interpretation of verified daily logs — working hours ≠ billable hours"
          statusTag={
            ts ? (
              <Tag color={HIRE_DOC_STATUS_COLOR[ts.status] || 'default'}>{ts.status}</Tag>
            ) : undefined
          }
          primaryActions={
            <Space wrap>
              <Button onClick={() => navigate('/asset/pmv/hire/timesheets')}>List</Button>
              <Button
                icon={<PrinterOutlined />}
                disabled={!ts}
                onClick={() => {
                  if (!ts) return;
                  const agreement = ts.hire_agreement_id
                    ? agreementById.get(ts.hire_agreement_id)
                    : undefined;
                  openHireTimesheetPrint({
                    timesheet: ts,
                    agreementLabel: agreement
                      ? formatHireAgreementOption(agreement)
                      : ts.hire_agreement_id,
                    supplierLabel: agreement
                      ? resolveLabel(supplierLabelById, agreement.supplier_id)
                      : undefined,
                    equipmentLabel: agreement
                      ? resolveLabel(equipmentLabelById, agreement.pmv_profile_id)
                      : undefined,
                  });
                }}
              >
                Print
              </Button>
              {ts?.hire_agreement_id ? (
                <Button
                  onClick={() => navigate(`/asset/pmv/hire/agreements/${ts.hire_agreement_id}`)}
                >
                  Agreement
                </Button>
              ) : null}
              <Button disabled={ts?.status !== 'DRAFT'} onClick={() => actionMut.mutate('submit')}>
                Submit
              </Button>
              <Button
                disabled={!['SUBMITTED', 'UNDER_REVIEW'].includes(String(ts?.status))}
                onClick={() => actionMut.mutate('review')}
              >
                Review
              </Button>
              <Button
                type="primary"
                disabled={ts?.status === 'CERTIFIED'}
                onClick={() => actionMut.mutate('certify')}
              >
                Certify
              </Button>
              {ts?.status === 'CERTIFIED' ? (
                <Button
                  onClick={() =>
                    navigate(`/asset/pmv/hire/statements?timesheet=${ts.id}&agreement=${ts.hire_agreement_id}`)
                  }
                >
                  Create statement
                </Button>
              ) : null}
            </Space>
          }
        />
        <Alert
          style={{ marginBottom: 12 }}
          type="info"
          showIcon
          message="Amounts are backend-calculated. Missing rate never displays as 0."
        />
        <Tabs
          items={[
            {
              key: 'header',
              label: 'Header',
              children: (
                <Card size="small">
                  <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered>
                    <Descriptions.Item label="Agreement">
                      {ts?.hire_agreement_id ? (
                        <Link to={`/asset/pmv/hire/agreements/${ts.hire_agreement_id}`}>
                          {agreement
                            ? formatHireAgreementOption(agreement)
                            : ts.hire_agreement_id.slice(0, 8)}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Equipment">
                      {agreement
                        ? resolveLabel(equipmentLabelById, agreement.pmv_profile_id)
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Supplier">
                      {agreement ? resolveLabel(supplierLabelById, agreement.supplier_id) : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Period">
                      {ts?.period_from || '—'} → {ts?.period_to || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Total">
                      {formatHireMoney(ts?.total_amount, ts?.currency)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Calculation">
                      {ts?.calculation_id ? (
                        <Text code>{ts.calculation_id.slice(0, 8)}…</Text>
                      ) : (
                        '—'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Notes" span={2}>
                      {ts?.notes || '—'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              ),
            },
            {
              key: 'lines',
              label: `Lines (${ts?.lines?.length ?? 0})`,
              children: (
                <Card size="small">
                  <Table
                    rowKey={(r, i) => String((r as { id?: string }).id || i)}
                    size="small"
                    pagination={false}
                    dataSource={ts?.lines ?? []}
                    locale={{ emptyText: <Empty description="No timesheet lines" /> }}
                    columns={[
                      { title: 'Date', dataIndex: 'operating_date', width: 110 },
                      { title: 'Op min', dataIndex: 'working_minutes', width: 80 },
                      { title: 'Billable', dataIndex: 'billable_quantity', width: 90 },
                      {
                        title: 'Amount',
                        dataIndex: 'line_amount',
                        render: (v) => formatHireMoney(v, ts?.currency),
                      },
                      { title: 'Note', dataIndex: 'explanation', ellipsis: true },
                    ]}
                  />
                </Card>
              ),
            },
            {
              key: 'explanation',
              label: 'Calculation',
              children: (
                <Row gutter={16}>
                  <Col xs={24} lg={12}>
                    <Card size="small" title="Summary">
                      {summaryEntries.length ? (
                        <Descriptions size="small" column={1} bordered>
                          {summaryEntries.slice(0, 16).map(([k, v]) => (
                            <Descriptions.Item key={k} label={k}>
                              {String(v)}
                            </Descriptions.Item>
                          ))}
                        </Descriptions>
                      ) : (
                        <Text type="secondary">No structured explanation on this timesheet.</Text>
                      )}
                    </Card>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card size="small" title="Raw explanation">
                      <Collapse
                        size="small"
                        items={[
                          {
                            key: 'raw',
                            label: 'JSON payload',
                            children: explanation ? (
                              <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', margin: 0 }}>
                                {JSON.stringify(explanation, null, 2)}
                              </pre>
                            ) : (
                              <Text type="secondary">None</Text>
                            ),
                          },
                        ]}
                      />
                      {ts?.calculation_id ? (
                        <Button
                          style={{ marginTop: 8 }}
                          type="link"
                          onClick={() =>
                            pmvHireService.getCalculation(ts.calculation_id!).then((c) => {
                              Modal.info({
                                title: `Calculation ${c.calculation_number || c.id.slice(0, 8)}`,
                                width: 720,
                                content: (
                                  <Descriptions size="small" column={1} bordered>
                                    <Descriptions.Item label="Period">
                                      {c.period_from} → {c.period_to}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Status">{c.status}</Descriptions.Item>
                                    <Descriptions.Item label="Total">
                                      {c.rate_available === false
                                        ? 'Unavailable (missing rate)'
                                        : formatHireMoney(c.total_amount, c.currency)}
                                    </Descriptions.Item>
                                  </Descriptions>
                                ),
                              });
                            })
                          }
                        >
                          Open calculation summary
                        </Button>
                      ) : null}
                    </Card>
                  </Col>
                </Row>
              ),
            },
          ]}
        />
      </WorkspaceLayout>
    );
  }

  const listItems = listQ.data?.items ?? [];

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Hire Timesheets"
        subtitle="Period commercial timesheets derived from hire calculations"
        primaryActions={
          <Space>
            <Button
              type="primary"
              onClick={() => {
                form.resetFields();
                setCreateOpen(true);
              }}
            >
              From calculation
            </Button>
            <Button onClick={() => navigate('/asset/pmv/hire/control')}>Hire Control</Button>
            <Button onClick={() => navigate('/asset/pmv/hire/statements')}>Statements</Button>
          </Space>
        }
        extra={
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={status}
            onChange={setStatus}
            options={['DRAFT', 'SUBMITTED', 'UNDER_REVIEW', 'CERTIFIED', 'RETURNED', 'VOID'].map(
              (v) => ({ value: v, label: v })
            )}
          />
        }
      />
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="Create from a hire calculation — never paste calculation UUIDs. Certify before statement."
      />
      {!listQ.isLoading && listItems.length === 0 ? (
        <Empty description="No timesheets yet">
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            Create from calculation
          </Button>
        </Empty>
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={listQ.isLoading}
          dataSource={listItems}
          pagination={{ pageSize: 25 }}
          scroll={{ x: 1180 }}
          tableLayout="fixed"
          columns={[
            {
              title: 'Timesheet',
              dataIndex: 'timesheet_number',
              width: 180,
              ellipsis: true,
              render: (v: string, r: PmvHireTimesheet) => (
                <div style={hireTableCellEllipsis}>
                  <Button
                    type="link"
                    style={hireTableLinkBtnStyle}
                    onClick={() => navigate(`/asset/pmv/hire/timesheets/${r.id}`)}
                  >
                    {v || r.id.slice(0, 8)}
                  </Button>
                </div>
              ),
            },
            {
              title: 'Agreement',
              dataIndex: 'hire_agreement_id',
              width: 200,
              ellipsis: true,
              render: (aid: string) => {
                const a = agreementById.get(aid);
                return (
                  <div style={hireTableCellEllipsis}>
                    {a ? (
                      <Link to={`/asset/pmv/hire/agreements/${aid}`}>{a.agreement_number}</Link>
                    ) : (
                      aid?.slice(0, 8) || '—'
                    )}
                  </div>
                );
              },
            },
            {
              title: 'Equipment',
              width: 220,
              ellipsis: true,
              render: (_: unknown, r: PmvHireTimesheet) => {
                const a = agreementById.get(r.hire_agreement_id);
                return (
                  <div style={hireTableCellEllipsis}>
                    {a ? resolveLabel(equipmentLabelById, a.pmv_profile_id) : '—'}
                  </div>
                );
              },
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 130,
              render: (s: string) => (
                <Tag color={HIRE_DOC_STATUS_COLOR[s] || 'default'}>{s}</Tag>
              ),
            },
            {
              title: 'Total',
              dataIndex: 'total_amount',
              width: 140,
              align: 'right' as const,
              render: (v, r) => formatHireMoney(v, r.currency),
            },
            {
              title: 'Period',
              width: 200,
              ellipsis: true,
              render: (_: unknown, r: PmvHireTimesheet) =>
                `${r.period_from || '—'} → ${r.period_to || '—'}`,
            },
          ]}
        />
      )}
      <Modal
        title="Create timesheet from calculation"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
        width={640}
        destroyOnClose
      >
        <Alert
          style={{ marginBottom: 12 }}
          type="info"
          showIcon
          message="Select agreement, then the calculation period. Amounts come from the backend calc engine."
        />
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) =>
            createMut.mutate({ calculation_id: v.calculation_id, notes: v.notes })
          }
        >
          <Form.Item
            name="hire_agreement_id"
            label="Hire agreement"
            rules={[{ required: true, message: 'Select an agreement' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Search agreement"
              options={agreementOptions}
              onChange={() => form.setFieldsValue({ calculation_id: undefined })}
            />
          </Form.Item>
          <Form.Item
            name="calculation_id"
            label="Calculation"
            rules={[{ required: true, message: 'Select a calculation' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={
                watchedAgreementId ? 'Select calculation period' : 'Select agreement first'
              }
              disabled={!watchedAgreementId}
              loading={calcsQ.isLoading}
              options={calcOptions}
              notFoundContent={
                watchedAgreementId && !calcsQ.isLoading
                  ? 'No calculations for this agreement'
                  : undefined
              }
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </WorkspaceLayout>
  );
};

export default PmvHireTimesheetPage;
